import { getPcStockUnits } from "@/utils/helpers/stock";
import {
  BookingStatus,
  ICreateAddons,
  ICreatePSS,
  IProductSlotState,
  IQuantifiedProductSlotState,
  IRemovePSS,
  PaymentStatus,
  PersonCategory,
  PriceCategory,
  PSSType,
  QRCodeTypes,
  RefundStatus,
  ScanningStatus,
  TicketSeasonalityType,
} from "@/types/db/product-slot-state";
import dayjs from "dayjs";
import { PSSErrors } from "@/constants/errors/product-slot-state";
import { holdRetails, holdTimeslots } from "@/db/timeslot";
import { createCompositeId, generateID } from "./app";
import { getLanguage } from "./lang";
import {
  IProductCategory,
  PCTypes,
  SeatType,
} from "@/types/db/product-category";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { partition } from "lodash";
import { SharedCallbacks } from "../shared-resources";
import randomstring from "randomstring";
import { sellOnSoldOut } from "./product-category";
import { LogLevel, LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import { randomIntFromInterval, sumArray } from "./common";
import PSSModule from "@/store/modules/pss";
import BookingModule from "@/store/modules/booking";
import NotificationService from "@/services/notification.service";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { AccountingState, PaymentMethods } from "@/types/db/accounting";
import { BookingChange } from "@/interfaces/store/IBooking";
import { isBelgianCompliant } from "../vue-helpers/compliance";
import { PromocodeCampaignExpiryTypes } from "@/types/db/promocode-campaign";
import { removeAndReapplyPromocodes } from "../vue-helpers/promocode";

export async function createPSS(data: ICreatePSS | ICreateAddons) {
  console.log(")------ hi from createPSS");
  const {
    pssType,
    bookingUuid,
    priceId,
    categoryId,
    priceValue,
    timeslot,
    productCategory,
    sellerId,
    count,
    discountValue,
    sendTicket,
    token,
    isAutomaticAddon,
    mainTicketUUID,
    mainSlot,
    packagePriceId,
    bookingReference,
    accountingValue,
    overridenPrice,
  } = data;
  console.log("-------- accountingValue");
  console.log(accountingValue);
  let { tokenEndDate } = data;

  const getGroupInfo = SharedCallbacks.getGroupInfo;
  const groupInfo = getGroupInfo && getGroupInfo();

  const price = productCategory.prices.find((p) => p.id === priceId);
  if (!price) {
    throw new Error(PSSErrors.PriceNotInProductCategory);
  }

  if (pssType === PSSType[PSSType.Ticket] && !timeslot) {
    throw new Error(PSSErrors.NoTSInPSS);
  }

  if (pssType === PSSType[PSSType.Retail] && price.retail.length === 0) {
    throw new Error(PSSErrors.NoRetailInPSS);
  }

  if (!groupInfo || !groupInfo.id) {
    throw new Error("No groupInfo set");
  }

  // make sure that we have enough availability
  // if the product is a ticket then we consider the availability of the timeslot
  // otherwise we consider psp.quantity and SellOnSoldOut option of the corresponding price
  let availability = 0;
  availability =
    pssType === PSSType[PSSType.Ticket]
      ? timeslot?.availability || 0
      : productCategory.prices.find((p) => p.id === priceId)?.quantity || 0;

  if (
    count > availability &&
    !sellOnSoldOut(productCategory.prices, productCategory.type) &&
    (!productCategory.stockIds ||
      !productCategory.stockIds.length ||
      !productCategory.stockIds.includes(-1000))
  ) {
    LogService.info("Not enough availabilities to create PSS", {
      priceId,
      count,
      availability,
    });
    NotificationService.showCustomMessageWithTitle(
      t("error.not-enough-availability"),
      t("common.warning"),
      "warning"
    );
    return [];
  }

  const pssList: IProductSlotState[] = [];

  if (pssType === PSSType[PSSType.Ticket] && timeslot) {
    await holdTimeslots(timeslot.id, count);
  } else if (pssType === PSSType[PSSType.Retail]) {
    await holdRetails(price.retail[0].id, count);
  }

  for (let i = 0; i < count; i++) {
    const createdAt = dayjs().toISOString();
    const uuid = generateID();
    const type = Number(PCTypes[productCategory.type as any]);

    const groupId = POSConfigModule.groupId;

    let pssStart = timeslot?.startDateTime;
    let pssEnd = timeslot?.endDateTime;
    if (productCategory.memberships && productCategory.memberships.length) {
      // Lets use backend logic when assigning pssStartDate and pssEnddate
      // https://smeetz.slack.com/archives/C04DH9WN0VD/p1682435809572059?thread_ts=1682094585.925019&cid=C04DH9WN0VD
      enum MembershipExpiryTypes {
        EXPIRY_TYPE_DATE = 1,
        EXPIRY_TYPE_DURATION = 2,
      }

      const memberships = [...productCategory.memberships];
      memberships.sort((m1, m2) => m1.id - m2.id);
      const usedMembership = memberships.find((m) => m.type === 1);

      if (usedMembership) {
        // assign pssStart
        if (usedMembership.startDate) {
          pssStart = usedMembership.startDate;
        } else {
          pssStart = dayjs().utc().toISOString();
        }

        // assign pssEnd
        if (
          usedMembership.expiryType === MembershipExpiryTypes.EXPIRY_TYPE_DATE
        ) {
          pssEnd = usedMembership.expiryDate;
        }
        if (
          usedMembership.expiryType ===
          MembershipExpiryTypes.EXPIRY_TYPE_DURATION
        ) {
          const months =
            usedMembership.periodLength * usedMembership.numberMandatoryPeriod;
          pssEnd = dayjs(pssStart).add(months, "month").utc().toISOString();
        }
        // for memberships, we need to use the expiryDate in the token.
        tokenEndDate = usedMembership.expiryDate;
      }
    }

    // For Gift Cards we need to consider the expiry type and period from the promocode campaign
    if (type === PCTypes.GiftCard) {
      // pssStart is now (purchase date)
      pssStart = dayjs().utc().toISOString();

      const promoCodeCampaign = productCategory.promoCodeCampaignList?.find(
        (c) => c.giftCardTicketId === productCategory.id && c.cancelled !== 1
      );
      if (promoCodeCampaign) {
        if (
          promoCodeCampaign.expiryType ===
          PromocodeCampaignExpiryTypes.EXPIRY_TYPE_DATE
        ) {
          // expiry date is the end date of the promocode campaign
          pssEnd = promoCodeCampaign.endDateTime;
        } else if (
          promoCodeCampaign.expiryType ===
          PromocodeCampaignExpiryTypes.EXPIRY_TYPE_DURATION
        ) {
          // expiry date is start date + duration (days)
          pssEnd = dayjs(pssStart)
            .add(promoCodeCampaign.expiryDays, "day")
            .utc()
            .toISOString();
        } else {
          pssEnd = promoCodeCampaign.endDateTime;
        }
      }
    }

    // If a Handshake Smart qr code then we need to generate a random barcode, because it will be used when generating the token
    let barcode = "";
    if (
      productCategory.qrCodeType === QRCodeTypes.HANDSHAKESMART ||
      POSConfigModule.hasSkidataIntegration ||
      groupId === 18793 ||
      groupId === 19579
    ) {
      barcode = String(randomIntFromInterval(1000000000, 9999999999));
    }

    const tokenEnd = tokenEndDate || pssEnd;
    let randomToken = "";
    if (
      productCategory.qrCodeType === QRCodeTypes.HANDSHAKESMART ||
      POSConfigModule.hasSkidataIntegration ||
      groupId === 18793 ||
      groupId === 19579
    ) {
      const price = productCategory.prices.find((p) => p.id === priceId);
      if (price) {
        randomToken = generateHandShakeSmartToken(
          productCategory,
          Number(price.priceCategory),
          Number(groupId),
          String(pssStart),
          barcode,
          bookingReference
        );
      }
    } else if (
      productCategory.qrCodeType === QRCodeTypes.CameParkare &&
      tokenEnd
    ) {
      randomToken = generateTokenType3(
        tokenEnd,
        Number(productCategory.multipleScan)
      );
    } else {
      // We're going to generate a random token that respects the token setting of the group
      if (POSConfigModule.groupInfo) {
        const groupInfo: any = POSConfigModule.groupInfo;
        if (
          groupInfo.itemTokenSettingList &&
          groupInfo.itemTokenSettingList.length
        ) {
          const tokenSetting: any = groupInfo.itemTokenSettingList[0];
          randomToken = randomstring.generate({
            length: tokenSetting.tokenLength || 50,
            charset: "alphanumeric",
          });
        } else {
          randomToken = randomstring.generate({
            length: 50,
            charset: "alphanumeric",
          });
        }
      } else {
        // there is no special setup for this group tokens, lets keep the old logic
        randomToken = randomstring.generate({
          length: 50,
          charset: "alphanumeric",
        });
      }
    }

    let productCustomText = "";
    if (
      productCategory.product.productBrandings &&
      productCategory.product.productBrandings[0] &&
      productCategory.product.productBrandings[0].customText
    ) {
      productCustomText =
        productCategory.product.productBrandings[0].customText;
    }

    const pss: IProductSlotState = {
      _id: createCompositeId([createdAt, uuid]),
      uuid: uuid,
      bookingUuid,
      orderSessionId: String(BookingModule.currentOrderSessionId),
      sellerUserId: sellerId,
      bookingStatus: BookingStatus.Pending,
      paymentStatus: PaymentStatus.NotStarted,
      scanningStatus: ScanningStatus.NotScanned,
      refundStatus: RefundStatus.None,
      scanningUserId: null,
      creationDateTime: createdAt,
      // canceledAt: null,
      // refundedAt: null,
      updatedAt: createdAt,
      startDateTime: pssStart,
      endDateTime: pssEnd,
      priceValue,
      accountingValue: accountingValue ?? undefined,
      priceId,
      priceName: price.nameLocale[getLanguage()] || price.name,
      paymentFeeBilled: 0,
      paymentFeeCollected: 0,
      paymentFeeEffective: 0,
      refundAmount: 0,
      // barCode: null,
      categoryId: categoryId,
      categoryName:
        productCategory.nameLocale[getLanguage()] || productCategory.name,
      productId: productCategory.product.id,
      type: type,
      //timeSlotId: timeslot?.id || null,
      productOwnerGroupId: groupInfo.id,
      _discountValue: discountValue ?? 0,
      pssType: pssType,
      sendTicket,
      mainSlot: mainSlot || null,
      packagePriceId: packagePriceId || null,
      token: sendTicket === 1 ? randomToken : token,
      isReflectedInLocalAvailability: false,
      stockIds: productCategory.stockIds,
      stockUnits: getPcStockUnits(productCategory.stockConsumptions),
      productName:
        productCategory.product.nameLocale[getLanguage()] ||
        productCategory.product.name,
      overridenPrice,
      productCustomText: productCustomText,
    };

    // check if this is a seating plan pss so that we give a seat value and update its Type
    if (pss.type === PCTypes.SeatedSub) {
      // giving PSS a seat value
      pss.seat = data.seatId;
      pss.eventKey = data.eventKey;
      pss.seatType = data.seatType;
      // I don't know why yet, I asked Sergey, but it seems backend is doing pss.type = 3 when it is GA or Seating Plan instead of pss.type = 31
      // https://smeetz.slack.com/archives/C01BPJKFTUM/p1728570418427609?thread_ts=1723715030.257499&cid=C01BPJKFTUM
      // For MembershipSeated parent (type 17), BE (ORD-557) expects pss.type = 17 to trigger handlePosMembership
      if (
        pss.seatType === SeatType.Seat ||
        pss.seatType === SeatType.GeneralAdmissionArea
      ) {
        pss.type =
          data.parentSeatingType === PCTypes.MembershipSeated
            ? PCTypes.MembershipSeated
            : PCTypes.Seated;
      }
      if (
        BookingModule.selectedBooking &&
        BookingModule.selectedBooking.seatsIoHoldToken &&
        BookingModule.selectedBooking.seatsIoHoldToken.holdToken
      ) {
        pss.holdToken = String(
          BookingModule.selectedBooking?.seatsIoHoldToken.holdToken
        );
      }
    }

    if (pssType === PSSType[PSSType.Ticket] && timeslot) {
      if (
        type === PCTypes.Normal &&
        timeslot.categoryId &&
        timeslot.categoryId !== categoryId
      ) {
        LogService.info(
          `Timeslot category mismatch on PSS creation, blocking the ticket`,
          {
            groupId: POSConfigModule.groupId,
            pssCategoryId: categoryId,
            tsCategoryId: timeslot.categoryId,
            tsId: timeslot.id,
          },
          LogLevel.Error
        );
        throw new Error(PSSErrors.TimeslotCategoryMismatch);
      }
      pss.timeSlotId = timeslot?.id;
    } else if (pssType === PSSType[PSSType.Retail]) {
      pss.retailOptionId = price.retail[0].id;
      pss.retailOptionName = price.retail[0].name;
    }

    // check if this pss is an automatic Addon then we give it the mainTicketUUID coming from it's main PSS
    if (mainTicketUUID) {
      pss.isAutomaticAddon = isAutomaticAddon;
      pss.mainTicketUUID = mainTicketUUID;
    }

    if (barcode) {
      pss.barCode = barcode;
    }

    pssList.push(pss);

    // lets add the addons of the created pss
    if (!("addons" in data)) continue;
    pss.hasAutomaticAddons = data.addons.some(
      (addon) => addon.isAutomaticAddon === true
    );
    pss.isChillonPackage = data.addons.some((addon) => addon.accountingValue);
    const mainTicketPriceValue = pss.priceValue;
    pss.accountingValue = mainTicketPriceValue;

    // Rule:
    // Sum of accounting values of all addons should be less or equal to the price of main ticket
    // Backend currently throw an error whenever this rule is not satisfied
    // Lets validate the rule and simulate Backend
    if (data.addons && data.addons.length) {
      const addonAccountingValues: number[] = data.addons
        .filter((a) => a.isAutomaticAddon)
        .map((a) => {
          if (a.accountingValue) return a.accountingValue;
          else return 0;
        });

      if (sumArray(addonAccountingValues) > pss.priceValue) {
        LogService.info(
          `Error when creating pss for priceId: ${pss.priceId}, because sum of addons accountingValue is greater than main pss.priceValue`,
          {
            priceId: pss.priceId,
            priceValue: pss.priceValue,
            automaticAddons: data.addons.filter((a) => a.isAutomaticAddon),
          },
          LogLevel.Error
        );
        NotificationService.showCustomMessageWithTitle(
          t("error.accounting-value-bad-config"),
          t("common.error"),
          "error"
        );
        return [];
      }
    }

    for (const addon of data.addons) {
      if (addon.sendTicket !== 1) {
        // lets save the main pss token and pass it to createPSS
        // to be considered when creating the pss of the addon
        addon.token = pss.token;
      }
      if (addon.isAutomaticAddon) {
        pss.accountingValue -= addon.accountingValue || 0;
      }
      // lets save the main pss uuid as mainTicketUUID
      // and pass it to createPSS to be considered when creating the pss of the addon
      addon.mainTicketUUID = pss.uuid as string;
      try {
        const addonsPSS = await createPSS(addon);
        pssList.push(...addonsPSS);
      } catch (error) {
        LogService.info(
          `Error while creating addon for main ticket pss: ${pss.uuid}`,
          { addon, error }
        );
      }
    }
  }
  LogService.info(
    `new PSS have been created, booking: ${bookingUuid}`,
    pssList
  );

  return pssList;
}

/**
 * VAT rate of a pss, as a percentage, from its cached extension.
 */
function getPssVatRate(pss: IProductSlotState): number {
  const rateMiliValue =
    PSSModule.pssExtensions[String(pss.uuid)]?.taxes?.[0]?.rateMiliValue || 0;
  return rateMiliValue / 10000;
}

/**
 * Whether a removal of this pss is already tracked, either in the store or in
 * the batch being built. When it is, the existing entry's quantity is
 * decremented instead (removal quantities are negative) so identical tickets
 * collapse into one change line.
 */
function bookingChangeExists(
  changes: BookingChange[],
  pss: IProductSlotState
): boolean {
  const matches = (change: BookingChange) =>
    change.categoryId === pss.categoryId.toString() &&
    change.unitPrice === getPSSValue(pss) / 100 &&
    change.vatRate === getPssVatRate(pss);

  const bookingUUID = pss.bookingUuid || "";
  const trackedChanges = BookingModule.bookingChanges[bookingUUID] || [];
  const trackedIndex = trackedChanges.findIndex(matches);
  if (trackedIndex > -1) {
    BookingModule.increaseBookingChangeQuantityByIndex({
      bookingUUID,
      changeIndex: trackedIndex,
    });
    return true;
  }

  const pendingIndex = changes.findIndex(matches);
  if (pendingIndex > -1) {
    changes[pendingIndex].quantity -= 1;
    return true;
  }
  return false;
}

/**
 * Records a removed ticket so it can be reported to the Belgian FDM in the sale
 * event. Returns null outside Belgium, or when the removal was folded into an
 * existing change.
 */
function createBookingChange(
  pss: IProductSlotState,
  changes: BookingChange[]
): BookingChange | null {
  // Only Belgium reports removals, so nothing else pays the cost of tracking them.
  if (!isBelgianCompliant.value) {
    return null;
  }
  if (bookingChangeExists(changes, pss)) {
    return null;
  }
  return {
    categoryId: pss.categoryId.toString(),
    quantity: -1,
    unitPrice: getPSSValue(pss) / 100,
    vatRate: getPssVatRate(pss),
    categoryName: pss.categoryName,
  };
}

/**
 * Removes data.count pss from a booking based on data criteria.
 * If we have pssUuids to be removed then we ignore the count and explicitly remove concerned pss
 * @param data
 * @param booking
 * @returns
 */
export function removePSSFromPSB(
  data: IRemovePSS,
  booking: IProductSlotBooking
) {
  const { pssList } = booking;

  // Make sure that we have pssList on booking
  if (!booking.pssList) {
    throw new Error(PSBErrors.HasNoPSS);
  }
  const { pssType, productCategoryId, timeslot, priceId, count, pssUuids } =
    data;

  if (pssUuids && pssUuids.length) {
    // We will explicitly remove the corresponding pss since their uuids are supplied
    const pssListToRemove = booking.pssList.filter((ps) =>
      pssUuids.join(" - ").includes(String(ps.uuid))
    );
    // lets make a copy of the booking pssList
    const pssListCopy = booking.pssList.slice();
    /** if there are failed Adyen payments that are linked to these tickets,
     * then the latter will have the status Abandoned and will not be removed from the booking
     * this will allow syncing the failed accountings and their pss to backend
     */
    const abandonedPssUuids: string[] = [];
    const changes: BookingChange[] = [];
    pssListToRemove.forEach((pssToRemove) => {
      // track the removal for the Belgian sale event, abandoned or not
      const change = createBookingChange(pssToRemove, changes);
      if (change) {
        changes.push(change);
      }
      const hasFailedAdyenAccountings = booking.accountings.some(
        (acc) =>
          !acc.id &&
          acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
          acc.state === AccountingState.Failed &&
          acc.accountingEntriesList.some(
            (ae) =>
              ae.productSlotStateUuid &&
              pssToRemove.uuid === ae.productSlotStateUuid
          )
      );
      if (!hasFailedAdyenAccountings) {
        return;
      }
      const pssToAbandon = pssListCopy.find(
        (pss) => pss.uuid === pssToRemove.uuid
      );
      if (!pssToAbandon) {
        return;
      }
      abandonedPssUuids.push(pssToAbandon.uuid as string);
    });

    if (changes.length) {
      BookingModule.addBookingChanges({
        bookingUUID: String(booking.uuid),
        changes,
      });
    }

    // if a pss was abandoned due to having linked adyen payments, we keep it in the pss list. Otherwise we remove it from the booking
    const newPssList: IProductSlotState[] = [];
    pssListCopy.forEach((pss) => {
      if (!pssListToRemove.includes(pss)) {
        newPssList.push(pss);
      }
      if (abandonedPssUuids.includes(pss.uuid as string)) {
        pss.bookingStatus = BookingStatus.Abandoned;
        newPssList.push(pss);
      }
    });
    booking.pssList = newPssList;

    // remove the deleted pss from the pss extensions module
    const removedPSSIds = pssListToRemove.map((pss) => pss.uuid as string);
    PSSModule.removePSSExtensionsBatch(removedPSSIds);
    LogService.info(
      `Removed ${count} PSS from booking: ${booking.uuid}, new pssList attached`,
      booking.pssList
    );

    return null;
  }
  const Id =
    pssType === PSSType[PSSType.Ticket] ? timeslot?.id : data.retailOptionId;

  // Get all the pss that correspond to the criteria;
  const [corresponding, nonCorresponding] = partition(pssList, (pss) => {
    // lets ignore addons
    if (pss.mainTicketUUID) return false;

    const {
      timeSlotId: ctimeslotId,
      priceId: cPriceId,
      categoryId,
      retailOptionId,
    } = pss;

    // priceId and categoryId are enough to differentiate pss of a given retailOption in case of merch product
    // In case of a ticket product we need to add timeslotId to the criteria
    return (
      ((pssType === PSSType[PSSType.Ticket] && ctimeslotId === Id) ||
        (pssType === PSSType[PSSType.Retail] && retailOptionId === Id)) &&
      categoryId === productCategoryId &&
      priceId === cPriceId
    );
  });

  // Make sure that we have enough pss to remove;
  // No need to throw an error and block the user if the pss to remove is an empty array (TODO: throw a warning in such a case)
  if (corresponding.length > 0 && corresponding.length < count) {
    throw new Error(PSBErrors.HasNoEnoughPSS);
  }

  // Drop count pss
  const removedPSSIds: string[] = [];
  const remainingPSS: IProductSlotState[] = [];
  const changes: BookingChange[] = [];

  corresponding.forEach((pss, index) => {
    if (
      pss.bookingStatus === BookingStatus.Confirmed ||
      pss.bookingStatus === BookingStatus.Abandoned
    ) {
      // skip confirmed/abandoned pss as they should not be removed from booking
      // we are sure that the count of removed pss is just enough to remove unconfirmed PSS only
      // because in the PCSelect, we already disabled user from decreasing the quantity of tickets below the number of confirmed pss
      return;
    }
    const hasFailedAdyenAccountings = booking.accountings.some(
      (acc) =>
        !acc.id &&
        acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
        acc.state === AccountingState.Failed &&
        acc.accountingEntriesList.some(
          (ae) =>
            ae.productSlotStateUuid && pss.uuid === ae.productSlotStateUuid
        )
    );
    if (index < count) {
      // track the removal for the Belgian sale event, abandoned or not
      const change = createBookingChange(pss, changes);
      if (change) {
        changes.push(change);
      }
    }

    if (hasFailedAdyenAccountings && index < count) {
      // if a removed pss is linked to a failed adyen accounting, set its status to abandoned and keep it in booking
      pss.bookingStatus = BookingStatus.Abandoned;
      remainingPSS.push(pss);
    } else if (index < count) {
      removedPSSIds.push(String(pss.uuid));
    } else {
      remainingPSS.push(pss);
    }
  });

  if (changes.length) {
    BookingModule.addBookingChanges({
      bookingUUID: String(booking.uuid),
      changes,
    });
  }

  // lets also remove any addon attached to the deleted pss
  const nonCorrespondingWithoutAddons = nonCorresponding.filter(
    (pss) => !pss.mainTicketUUID || !removedPSSIds.includes(pss.mainTicketUUID)
  );
  booking.pssList = nonCorrespondingWithoutAddons.concat(remainingPSS);

  removeAndReapplyPromocodes(booking);

  // remove deleted pss from pss extensions
  PSSModule.removePSSExtensionsBatch(removedPSSIds);
  LogService.info(
    `Removed ${count} PSS from booking: ${booking.uuid}, new pssList attached`,
    booking.pssList
  );

  return null;
}

export function releaseTimeslots(data: { id: number; count: number }) {
  return null;
}

export function savePSS() {
  return null;
}

// TODO Hamza: Take time later to remove the below function we have an identical one in vue-helpers file
export function groupedReceiptPSS(
  booking: IProductSlotBooking,
  options: {
    includePending?: boolean;
    /** narrows the grouping to a subset, e.g. one order session's tickets */
    pssList?: IProductSlotState[];
    /**
     * Adds booking status to the grouping key, so cancelled and confirmed
     * tickets of the same category never share a group. Belgian receipts print
     * a cancelled group as a single negative line, which would be wrong for a
     * group that mixed the two.
     */
    byBookingStatus?: boolean;
  } = {}
): IQuantifiedProductSlotState[] {
  const groupedPSSList: IQuantifiedProductSlotState[] = [];
  const pssList = options.pssList ?? booking.pssList;

  for (let i = 0; i < pssList.length; i++) {
    if (
      (pssList[i].bookingStatus === BookingStatus.Pending &&
        !options.includePending) ||
      pssList[i].bookingStatus === BookingStatus.Abandoned
    ) {
      // we don't consider pending/abandoned pss
      // include pending is used when printing draft receipt
      continue;
    }
    if (pssList[i].isAutomaticAddon && pssList[i].accountingValue) {
      // we don't print automatic addon pss that have an accounting value (Chillon's case)
      continue;
    }
    // pss are grouped by category, price and timeslot
    const sameBookingStatus = (pss: IQuantifiedProductSlotState) =>
      options.byBookingStatus
        ? pss.bookingStatus === pssList[i].bookingStatus
        : true;
    let groupedPSS = groupedPSSList.find(
      (pss) =>
        ((pssList[i].pssType === PSSType[PSSType.Ticket] &&
          pss.categoryId === pssList[i].categoryId &&
          pss.priceId === pssList[i].priceId &&
          pss.timeSlotId === pssList[i].timeSlotId) ||
          (pssList[i].pssType === PSSType[PSSType.Retail] &&
            pss.categoryId === pssList[i].categoryId &&
            pss.priceId === pssList[i].priceId &&
            pss.retailOptionId === pssList[i].retailOptionId)) &&
        sameBookingStatus(pss)
    );
    if (!groupedPSS) {
      // This pss is not grouped yet, lets add it with quantity = 1 and its pssID
      groupedPSS = Object.assign(
        {
          optionName: pssList[i].retailOptionName,
          quantity: 1,
          pssIds: [String(pssList[i].uuid)],
          pcType: pssList[i].type,
        },
        pssList[i]
      );
      groupedPSSList.push(groupedPSS);
    } else {
      // This pss is already grouped, lets increase its quantity by one and add this new pssID
      groupedPSS.quantity = groupedPSS.quantity + 1;
      groupedPSS.pssIds.push(String(pssList[i].uuid));
    }
  }

  return groupedPSSList;
}

/**
 * generates and return a pss token of type 3
 * token type 3 has the following format:
 * Single entrance (1 scan) XXXXXXXXXXXXXXXXX=AAMMJJST4P9634
 * Limited number of scans XXXXXXXXXXXXXXXXX=AAMMJJST5P9635
 * Unlimited scans XXXXXXXXXXXXXXXXX=AAMMJJST7P9637
 * YYMMDD is the endDate of the pss
 * @param endDate
 * @param multipleScan
 */
export function generateTokenType3(
  endDate: string,
  multipleScan: number
): string {
  const date = dayjs(endDate).format("YYMMDD");
  const randomNumber = randomstring.generate({
    length: 17,
    charset: "numeric",
  });
  if (multipleScan < 0) {
    // unlimited scans
    return `${randomNumber}=${date}ST7P9637`;
  }
  if (multipleScan > 0) {
    // limited number of scans
    return `${randomNumber}=${date}ST5P9635`;
  }
  // single entrance (1 scan)
  return `${randomNumber}=${date}ST4P9634`;
}

/**
 *
 * @returns Generates and return a token of type Handshake Smart
 */
export function generateHandShakeSmartToken(
  productCategory: IProductCategory,
  priceCategory: number,
  groupId: number,
  startDateTimeTz: string,
  barCode: string,
  bookingReference: string
) {
  if (!priceCategory) {
    // priceCategory is 0 only if user still has default value for this field assigned by the migration
    // user just needs to refresh config to get the real value if priceCategory
    // lets return an empty token until then
    return "";
  }

  const categoryId = productCategory.id;
  const pcType = Number(productCategory.type);

  const handhakeConfig = productCategory.handshakeCategoryTsproperty;
  let token = "";

  //
  let seasonalityType = TicketSeasonalityType.TICKET_TYPE_1_DAY.toString();
  if (handhakeConfig) {
    seasonalityType = handhakeConfig.ticketTypeCode;
  } else if (
    pcType === PCTypes.Membership ||
    pcType === PCTypes.MembershipSeated ||
    pcType === PCTypes.MembershipVoucher
  ) {
    seasonalityType = TicketSeasonalityType.TICKET_TYPE_SEASON.toString();
  }

  //
  let personCategory = "";
  switch (priceCategory) {
    case PriceCategory.CATEGORY_ADULT:
      personCategory = PersonCategory.PERSON_CATEGORY_ADULT;
      break;
    case PriceCategory.CATEGORY_REDUCED:
      personCategory = PersonCategory.PERSON_CATEGORY_REDUCED;
      break;
    case PriceCategory.CATEGORY_CHILD:
      personCategory = PersonCategory.PERSON_CATEGORY_CHILD;
      break;
    case PriceCategory.CATEGORY_OTHERS:
      personCategory = PersonCategory.PERSON_CATEGORY_OTHER;
      break;
  }

  // check if we need to start the scan XX minutes before the start date
  const scanOpeningMinutesBeforeStart =
    productCategory.categoryInfo?.scanOpeningMinutesBeforeStart || 0;

  const formatedDate = dayjs(startDateTimeTz)
    .subtract(scanOpeningMinutesBeforeStart, "minute")
    .format("YYMMDDHHmm");

  //
  const catIdStr = "000000" + categoryId;
  const formatedCategoryId = catIdStr.substring(String(catIdStr).length - 6);

  //
  const barCodeStr = "00000000" + barCode;
  const formatedBarCode = barCodeStr.substring(String(barCodeStr).length - 8);

  //
  const referenceStr = "00000000" + bookingReference;
  const formatedReference = referenceStr.substring(
    String(referenceStr).length - 8
  );

  //
  const area = handhakeConfig?.areaCode || "000";

  //
  const groupIdSubStr = String(groupId).substring(String(groupId).length - 3);
  const seasonalityTypeSubStr = seasonalityType.substring(0, 4);
  const personCategorySubStr = personCategory.toString().substring(0, 4);
  token =
    "S1" +
    groupIdSubStr +
    seasonalityTypeSubStr +
    personCategorySubStr +
    formatedDate +
    formatedCategoryId +
    area +
    formatedBarCode +
    formatedReference;
  return token;
}

// returns accountingValue if its an automatically booked addon
// otherwise returns priceValue
export function getPSSValue(
  pss: IProductSlotState | IQuantifiedProductSlotState
) {
  if (pss.hasAutomaticAddons && pss.isChillonPackage) {
    // if main ticket with automatic addons, with some addons containing accountingValue (Chillon's case)
    return pss.accountingValue || 0;
  } else if (pss.isAutomaticAddon && pss.accountingValue) {
    // if pss is an automatic addon, contains an accounting value (Chillon's case)
    return pss.accountingValue;
  } else {
    return pss.priceValue;
  }
}

// returns the total of a package that contains a main ticket and automatic addons
// in case of Chillon's package, it uses accountingValues, otherwise priceValues
export function getPackageTotalValue(
  mainTicket: IProductSlotState | IQuantifiedProductSlotState
) {
  if (!mainTicket.isChillonPackage) {
    return getPSSValue(mainTicket);
  }
  let total = getPSSValue(mainTicket);
  BookingModule.selectedBooking?.pssList.forEach((pss) => {
    if (pss.mainTicketUUID === mainTicket.uuid) {
      total += getPSSValue(pss);
    }
  });
  return total;
}
